import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { AxiosError } from 'axios'

import { LoginForm, Logo } from '@/components'
import { usePublicApi, CreateSessionRequestBody } from '@/hooks'
import { LEGACY_URL_PLACEHOLDER } from '@/utils/constants'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'

type LoginFormContainerProps = {
    onSuccess?: () => void
}

export const LoginFormContainer: React.FC<LoginFormContainerProps> = (
    props
) => {
    const { onSuccess } = props
    const [errors, setErrors] = useState<Record<string, string>>({})
    const { useLoginMutation } = usePublicApi()
    const { mutate: login, isPending, reset } = useLoginMutation()
    const { t } = useTranslation('translations')
    const { setSession } = useAuthStore()

    const handleLogin = (data: CreateSessionRequestBody) => {
        setErrors({})
        login(data, {
            onSuccess: (response) => {
                setSession(response)
                onSuccess?.()
            },
            onError: (error) => {
                logLoginFailure(error)

                logLoginFailure(error)

                setErrors({
                    ERROR: getErrorMessage(error, t),
                })

                reset()
            },
        })
    }

    return (
        <div className="space-y-6">
            <Logo className="h-logoHeight w-auto" />
            <div className="text-heading-5">{t('login_form.title')}</div>
            <LoginForm
                usernameInputLabel={t('login_form.fields.username.label')}
                usernameInputPlaceholder={t(
                    'login_form.fields.username.placeholder'
                )}
                passwordInputLabel={t('login_form.fields.password.label')}
                passwordInputPlaceholder={t(
                    'login_form.fields.password.placeholder'
                )}
                submitButtonLabel={t('login_form.fields.submit.label')}
                showPasswordInputLabel={t(
                    'login_form.fields.showPassword.label'
                )}
                isPendingSubmit={isPending}
                errors={errors}
                onSubmit={handleLogin}
            />
        </div>
    )
}

const getErrorMessage = (
    error: AxiosError<unknown, unknown>,
    t: ReturnType<typeof useTranslation>['t']
): string => {
    const status = error.response?.status

    if (status === 400) {
        return t('login_form.errors.credentials_error', {
            legacyAppUrl: LEGACY_URL_PLACEHOLDER,
        })
    }

    return t('login_form.errors.other_error', {
        legacyAppUrl: LEGACY_URL_PLACEHOLDER,
    })
}

const logLoginFailure = (error: unknown) => {
    const status =
        error instanceof AxiosError ? error.response?.status : undefined

    Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Login attempt failed',
        level: status && status >= 500 ? 'error' : 'warning',
        data: {
            status,
        },
    })
}
